import type { FeedItem, FeedPost, FeedPortfolio, FeedUser } from "../types/feed.ts"
import { normalizePostLikes } from "./normalizePostLikes.ts"
import { pickPostCommentsFromApi } from "./pickPostCommentsFromApi.ts"

type ApiUser = {
  id: number
  name?: string
  lastName?: string
  imageProfile?: string | null
}

function mapUser(u: ApiUser | undefined | null): FeedUser {
  if (!u) return { id: 0 }
  return {
    id: u.id,
    name: u.name,
    last_name: u.lastName,
    image_profile: u.imageProfile ?? undefined,
  }
}

function pickExploreVideoUrl(r: Record<string, unknown>): string | null {
  const coverVideo = r.coverVideo
  if (typeof coverVideo === "string" && coverVideo.trim()) return coverVideo.trim()
  const videoUrl = r.videoUrl
  if (typeof videoUrl === "string" && videoUrl.trim()) return videoUrl.trim()
  const video = r.video
  if (typeof video === "string" && video.trim()) return video.trim()
  if (Array.isArray(video) && video.length > 0) {
    const first = video[0]
    if (typeof first === "string" && first.trim()) return first.trim()
    if (first != null && typeof first === "object") {
      const block = first as Record<string, unknown>
      const nested = block.video ?? block.url
      if (typeof nested === "string" && nested.trim()) return nested.trim()
    }
  }
  return null
}

function parseLikedByCurrentUser(r: Record<string, unknown>): boolean | undefined {
  const v =
    r.likedByMe ??
    r.userHasLiked ??
    r.isLiked ??
    r.currentUserLiked ??
    r.liked_by_me
  if (typeof v === "boolean") return v
  if (typeof v === "string") {
    const low = v.trim().toLowerCase()
    if (low === "true" || low === "1") return true
    if (low === "false" || low === "0") return false
  }
  if (typeof v === "number") return v !== 0
  return undefined
}

export function normalizeExploreFeedItem(raw: unknown): FeedItem {
  const r = raw as Record<string, unknown>
  const itemType = r.itemType as string
  if (itemType === "post") {
    const user = r.user as ApiUser | undefined
    const coverImage = r.coverImage as string | null | undefined
    const exploreVideoUrl = pickExploreVideoUrl(r)
    const rawLikes = normalizePostLikes(r.likes)
    const countFromApi =
      typeof r.likesCount === "number"
        ? r.likesCount
        : typeof r.likes_count === "number"
          ? r.likes_count
          : Number(r.likesCount ?? r.likes_count ?? 0)
    const likedByCurrentUser = parseLikedByCurrentUser(r)
    const commentList = pickPostCommentsFromApi(r)
    const post: FeedPost = {
      id: Number(r.id),
      content: String(r.content ?? ""),
      createdAt: String(r.createdAt ?? ""),
      user: mapUser(user),
      comments: commentList,
      commentsCount:
        typeof r.commentsCount === "number"
          ? r.commentsCount
          : typeof r.comments_count === "number"
            ? r.comments_count
            : commentList.length,
      likes: rawLikes,
      likesCount: Math.max(countFromApi, rawLikes.length),
      ...(likedByCurrentUser !== undefined ? { likedByCurrentUser } : {}),
      images: coverImage ? [{ image: coverImage }] : [],
      video: exploreVideoUrl ? [{ video: exploreVideoUrl }] : [],
      itemType: "post",
    }
    return post
  }
  const user = r.user as ApiUser | undefined
  const coverImage = r.coverImage as string | null | undefined
  const portfolio: FeedPortfolio = {
    id: Number(r.id),
    name: String(r.name ?? ""),
    description: String(r.description ?? ""),
    type: r.type as "producto" | "servicio",
    zone: (r.zone as string) ?? undefined,
    price: r.price != null ? Number(r.price) : undefined,
    priceUnit: (r.priceUnit as string) ?? undefined,
    currencyType: (r.currencyType as string) ?? undefined,
    createdAt: String(r.createdAt ?? ""),
    user: mapUser(user),
    portfolio_images: coverImage ? [{ image: coverImage }] : [],
    itemType: "portfolio",
  }
  return portfolio
}
